import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'gatsby';
import { useSelector } from 'react-redux';
import { Badge, Button, classNames, Icon } from '@pella/sash-framework';
import { useOktaAuth } from '../../../../hooks/use-okta-auth';

import './index.css';

const STLPinpoint = ({ tile, onPointHover, activeHover, showTile, className }) => {
	const [tileData, setTileData] = useState(tile);
	const ecomData = useSelector((state) => state.ecommData);
	const { profile } = useOktaAuth();

	useEffect(() => {
		if (tile?.backend_name && !tile.manual) {
			if (ecomData?.tiles?.find((item) => item.backend_name === tile.backend_name)) {
				const tileFromRedux = ecomData.tiles.find((item) => item.backend_name === tile.backend_name);

				setTileData((current) => ({
					...current,
					...tileFromRedux,
				}));
			}
		}
	}, [tile, ecomData?.tiles]);

	useEffect(() => {
		const handleReposition = () => {
			const pins = document.querySelectorAll('.component-stl-pinpoint');

			pins.forEach((pin) => {
				if (pin.getBoundingClientRect().top < 300) {
					pin.querySelector('.pinpoint-tooltip').classList.add('tooltip-bottom');
				}
			});
		};

		handleReposition();

		window.addEventListener('resize', handleReposition);
		return () => window.removeEventListener('resize', handleReposition);
	}, []);

	const onPointEnter = () => {
		if (typeof onPointHover === 'function') onPointHover(tileData.backend_name);
		return;
	};

	const onPointLeave = () => {
		if (typeof onPointHover === 'function') onPointHover(null);
		return;
	};

	const buttonClasses = classNames({
		'pinpoint-trigger grid h-7 w-7 origin-center items-center justify-center rounded-full shadow-[0_3px_3px_rgba(0,0,0,0.25)] backdrop-blur transition-transform duration-300': true,
		'scale-[1.25] bg-primary shadow-[0_1.5px_3px_rgba(0,0,0,0.25)]': activeHover === tileData.backend_name,
		'bg-black/50 group-focus-within/pins:scale-[1.25] group-focus-within/pins:bg-primary group-focus-within/pins:shadow-[0_1.5px_3px_rgba(0,0,0,0.25)] group-hover/pins:scale-[1.25] group-hover/pins:bg-primary group-hover/pins:shadow-[0_1.5px_3px_rgba(0,0,0,0.25)]': activeHover !== tileData.backend_name,
	});

	const tooltipClasses = classNames({
		'pinpoint-tooltip absolute left-1/2 min-w-max -translate-x-1/2 bg-black/70 p-3 backdrop-blur transition-opacity duration-300 after:absolute after:h-10 after:w-full after:left-0 after:content-[""] z-2 bottom-full after:top-full mb-3': true,
		'visible opacity-100': activeHover === tileData.backend_name,
		'invisible opacity-0 group-focus-within/pins:visible group-focus-within/pins:opacity-100 group-hover/pins:visible group-hover/pins:opacity-100': activeHover !== tileData.backend_name,
		'md:grid md:grid-cols-[1fr_auto] md:gap-x-2 md:py-1 md:pl-5 md:pr-1.5': showTile,
		'grid grid-cols-[1fr_auto] gap-x-2 py-1 pl-5 pr-1.5': !showTile,
	});

	return (
		<div
			className={`component-stl-pinpoint group/pins ${className || ''}`}
			onMouseEnter={onPointEnter}
			onMouseLeave={onPointLeave}
			data-testid='stl-pinpoint'
		>
			<button
				type='button'
				className={buttonClasses}
				aria-label={`${tileData?.header} ${tileData?.display_name}`}
			>
				{activeHover !== tileData.backend_name && <span className='bg-primary absolute left-1/2 top-1/2 h-[14px] w-[14px] -translate-x-1/2 -translate-y-1/2 rounded-full group-focus-within/pins:hidden group-hover/pins:hidden' />}

				<Icon
					name='sell'
					className={`transition-opacity ${activeHover === tileData.backend_name ? 'visible opacity-100' : 'invisible opacity-0 group-focus-within/pins:visible group-focus-within/pins:opacity-100 group-hover/pins:visible group-hover/pins:opacity-100'}`}
					size={17}
					weight={700}
				/>
			</button>

			<div className={tooltipClasses}>
				<span className={`pinpoint-tooltip-tail absolute left-1/2 top-full h-2.5 w-10 -translate-x-1/2 border-x-[20px] border-t-[6px] border-transparent border-t-black/70 backdrop-blur`} />

				<p className={`text-overline m-0 text-white ${showTile ? 'md:col-start-1 md:row-start-1' : 'col-start-1 row-start-1'}`}>{tileData?.header}</p>
				<p className={`text-caption m-0 text-white ${showTile ? 'md:col-start-1 md:row-start-2' : 'col-start-1 row-start-2'}`}>{tileData?.display_name}</p>

				<Icon
					name='keyboard_arrow_right'
					className={`col-start-2 row-span-2 row-start-1 self-center text-white ${showTile ? 'hidden md:block' : 'block'}`}
				/>

				{showTile && !tileData?.manual && (
					<Badge
						color='primary'
						className='my-1 uppercase !tracking-[1.5px] md:hidden'
					>
						{!profile.isPro ? 'Online Price From' : 'Pro Online Price'}
					</Badge>
				)}

				{showTile && !tileData?.manual && (
					<p className='mt-1 flex items-center text-white md:hidden'>
						{profile.isPro && tileData?.price?.professional?.formatted && <span>{tileData.price.professional.formatted}</span>}
						<span className={profile.isPro && tileData?.price?.professional?.formatted ? 'text-neutral-300 line-through' : ''}>{tileData?.price?.consumer?.formatted}</span>
					</p>
				)}

				<Button
					color='tertiary'
					component={Link}
					href={tileData?.static_url}
					aria-label={`${tileData?.header} - ${tileData?.display_name}`}
					className={`flex items-center text-white decoration-white ${showTile ? 'md:-indent-full max-md:mt-1 max-md:!w-fit md:absolute md:left-0 md:top-0 md:h-full md:w-full' : '-indent-full absolute left-0 top-0 h-full !w-full'}`}
					passTo
				>
					<span className='!m-0 flex-1'>View details</span>
					<Icon
						name='keyboard_arrow_right'
						className='float-right'
					/>
				</Button>
			</div>
		</div>
	);
};

STLPinpoint.propTypes = {
	tile: PropTypes.object,
	className: PropTypes.string,
	onPointHover: PropTypes.func,
	activeHover: PropTypes.string,
	showTile: PropTypes.bool,
};

export default STLPinpoint;
